import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2, MapPin, MessagesSquare, Plus, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { getClientes } from "@/lib/api";
import { nombreVisible, nombreLegal, coincideBusqueda } from "@/lib/clienteNombre";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { NuevoClienteDialog } from "@/components/clientes/NuevoClienteDialog";
import { ZohoSyncDialog } from "@/components/clientes/ZohoSyncDialog";
import { cn } from "@/lib/utils";

const FILTROS = [
  { key: "activo", label: "Activos" },
  { key: "inactivo", label: "Inactivos" },
  { key: "chat", label: "Con chat" },
  { key: "todos", label: "Todos" },
];

const FILTRO_STORAGE_KEY = "clientes_filtro";

function ClienteCard({ cliente, onClick }) {
  const legal = nombreLegal(cliente);
  const inactivo = cliente.estado === "inactivo";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full min-w-0 overflow-hidden border rounded-lg p-4 bg-card transition-colors text-left flex gap-4 items-center h-full hover:border-muted-foreground/30",
        inactivo && "opacity-60"
      )}
    >
      <ClienteLogo cliente={cliente} size={56} />
      <div className="min-w-0 flex-1">
        <h3 className="font-medium text-sm truncate">{nombreVisible(cliente)}</h3>
        {legal && <p className="text-xs text-muted-foreground truncate mt-0.5">{legal}</p>}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {cliente.ciudad && (
            <span className="text-xs text-muted-foreground/80 flex items-center gap-1 min-w-0">
              <MapPin className="w-3 h-3 shrink-0" strokeWidth={1.5} />
              <span className="truncate max-w-[160px]">{cliente.ciudad}</span>
            </span>
          )}
          {inactivo && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Inactivo</span>}
          {cliente.chat_activo && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-primary/10 text-primary flex items-center gap-1">
              <MessagesSquare className="w-3 h-3" /> Chat
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ClientesPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState(() => {
    try {
      return localStorage.getItem(FILTRO_STORAGE_KEY) || "activo";
    } catch {
      return "activo";
    }
  });
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.permissions?.clientes === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const cargar = () => {
    setLoading(true);
    getClientes()
      .then((d) => setClientes(d.clientes || []))
      .catch(() => toast.error("Error al cargar clientes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user.permissions?.clientes !== false) cargar();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarFiltro = (f) => {
    setFiltro(f);
    try {
      localStorage.setItem(FILTRO_STORAGE_KEY, f);
    } catch (_) {}
  };

  const contadores = useMemo(
    () => ({
      activo: clientes.filter((c) => c.estado !== "inactivo").length,
      inactivo: clientes.filter((c) => c.estado === "inactivo").length,
      chat: clientes.filter((c) => c.chat_activo).length,
      todos: clientes.length,
    }),
    [clientes]
  );

  const visibles = useMemo(
    () =>
      clientes.filter((c) => {
        if (filtro === "activo" && c.estado === "inactivo") return false;
        if (filtro === "inactivo" && c.estado !== "inactivo") return false;
        if (filtro === "chat" && !c.chat_activo) return false;
        return coincideBusqueda(c, q);
      }),
    [clientes, filtro, q]
  );

  if (authLoading || !user) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppMainHeader title="Clientes" />
      <main className="w-full px-4 md:px-6 py-6 pb-10 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, NIF, ciudad…" className="pl-9 h-10" />
          </div>
          <div className="flex rounded-md border border-input bg-background p-0.5 overflow-x-auto">
            {FILTROS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => cambiarFiltro(f.key)}
                className={cn(
                  "px-3 h-9 sm:h-8 text-xs rounded whitespace-nowrap transition-colors",
                  filtro === f.key ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {f.label} <span className="tabular-nums opacity-60">{contadores[f.key]}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 md:ml-auto">
            <Button variant="outline" onClick={() => setSyncOpen(true)} className="gap-2 h-10 flex-1 md:flex-none">
              <RefreshCw className="w-4 h-4" /> Sincronizar con Zoho
            </Button>
            <Button onClick={() => setNuevoOpen(true)} className="gap-2 h-10 flex-1 md:flex-none">
              <Plus className="w-4 h-4" /> Nuevo cliente
            </Button>
          </div>
        </div>

        {loading && clientes.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : clientes.length === 0 ? (
          <div className="text-center py-24 space-y-3">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50" strokeWidth={1} />
            <p className="text-sm text-muted-foreground">Aún no hay clientes</p>
            <p className="text-xs text-muted-foreground">Importa el CSV de contactos de Zoho para empezar.</p>
            <Button onClick={() => setSyncOpen(true)} className="gap-2 mt-2">
              <RefreshCw className="w-4 h-4" /> Sincronizar con Zoho
            </Button>
          </div>
        ) : visibles.length === 0 ? (
          <p className="text-center py-24 text-sm text-muted-foreground">Ningún cliente coincide con la búsqueda.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibles.map((c, index) => (
              <div key={c.id} className="animate-card-in h-full" style={{ animationDelay: `${Math.min(index, 20) * 25}ms` }}>
                <ClienteCard cliente={c} onClick={() => navigate(`/clientes/${c.id}`)} />
              </div>
            ))}
          </div>
        )}
      </main>
      <NuevoClienteDialog open={nuevoOpen} onOpenChange={setNuevoOpen} onCreated={(c) => navigate(`/clientes/${c.id}`)} />
      <ZohoSyncDialog open={syncOpen} onOpenChange={setSyncOpen} onDone={cargar} />
    </div>
  );
}
